import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import CallexLoader from '../../../components/CallexLoader';

const AiTranscriptModal = ({ isOpen, onClose, data, isLoading, onRefresh }) => {
  const chatBottomRef = useRef(null);

  useEffect(() => {
    if (isOpen && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data, isOpen]);

  const [refreshKey, setRefreshKey] = useState(0);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    let interval;
    if (isOpen) {
      interval = setInterval(() => {
        onRefreshRef.current();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isOpen, refreshKey]);

  const handleManualRefresh = () => {
    if (!isLoading) {
      onRefresh();
      setRefreshKey((prev) => prev + 1);
    }
  };

  const messages = useMemo(() => {
    const rawMessages = data || [];
    return rawMessages.map((msg) => ({
      type: msg.role === 'ai' || msg.role === 'model' ? 'assistant' : 'user',
      text: msg.text,
      timestamp: msg.timestamp
        ? msg.timestamp > 10000000000
          ? msg.timestamp
          : msg.timestamp * 1000
        : Date.now()
    }));
  }, [data]);

  const renderChat = () => {
    if (!messages.length)
      return (
        <div
          className="p-3 text-center d-flex align-items-center justify-content-center"
          style={{
            minHeight: 'calc(100vh - 210px)'
          }}>
          No transcript available
        </div>
      );

    return (
      <div
        className="chat-wrapper custom-scrollbar"
        style={{
          minHeight: 'calc(100vh - 210px)',
          maxHeight: 'calc(100vh - 210px)',
          overflowY: 'auto',
          padding: '15px'
        }}>
        {messages.map((msg, index) => (
          <div key={index} className={`chat-row ${msg.type}`}>
            {msg.type === 'assistant' && (
              <div className="avatar assistant shadow-sm">
                <Icon.Headphones size={16} />
              </div>
            )}

            <div className="message-group">
              <div className="chat-bubble shadow-sm">{msg.text}</div>
              <div className="chat-time">
                {new Date(msg.timestamp).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>

            {msg.type === 'user' && (
              <div className="avatar user shadow-sm">
                <Icon.User size={16} />
              </div>
            )}
          </div>
        ))}
        <div ref={chatBottomRef} />
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered className="ai-transcript-modal">
      <ModalHeader toggle={onClose}>
        <div className="fs-5">Live Transcription</div>
      </ModalHeader>
      <ModalBody>
        {/* ================= AGENT & REFRESH ROW ================= */}
        <Row className="align-items-center mb-3">
          <Col md="6">
            <div className="d-flex align-items-center">
              <div
                className="bg-primary-light p-2 rounded-circle me-2 d-flex align-items-center justify-content-center"
                style={{ width: '36px', height: '36px' }}>
                <Icon.User size={18} className="text-primary" />
              </div>
              <h5 className="mb-0 text-dark fw-bold">{data?.agentName || 'AI Agent'}</h5>
            </div>
          </Col>
          <Col md="6" className="text-end">
            <Icon.RefreshCw
              size={20}
              className={`cursor-pointer text-primary me-2 ${isLoading ? 'rotate' : ''}`}
              onClick={handleManualRefresh}
            />
          </Col>
        </Row>

        {/* ================= CHAT SECTION ================= */}
        <div className="border rounded bg-white shadow-sm overflow-hidden">
          {isLoading && !data ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '400px' }}>
              <CallexLoader />
            </div>
          ) : (
            renderChat()
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

AiTranscriptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired
};

export default AiTranscriptModal;
